import type { MouseEvent, ReactElement, ReactNode } from 'react';

export interface ClickableRowProps {
  /** Opens the record. */
  onOpen: () => void;
  children: ReactNode;
}

/**
 * A table row that opens its record when clicked anywhere.
 *
 * **The row keeps its row semantics.** An earlier version put `role="button"`
 * and `tabIndex` on the `<tr>` to make it keyboard-reachable, which looked like
 * an improvement and was the opposite: an explicit role replaces the implicit
 * `row`, so the element leaves the table structure entirely and a screen-reader
 * user loses the ability to navigate the table by row and column at all. It also
 * nests a button inside a button, since every row already contains its own
 * controls.
 *
 * So the click handler here is a convenience for a pointer, and the keyboard
 * path is a real control *inside* the row — see `RowTitle`. Every row that uses
 * this must render one, or the record becomes unreachable without a mouse.
 *
 * The guard is the important part. The row contains its own buttons (Edit,
 * Delete, Update status), and their clicks bubble. Without it, deleting a record
 * would also open it, and the confirmation would appear behind the detail view.
 */
export function ClickableRow({ onOpen, children }: ClickableRowProps): ReactElement {
  function onClick(event: MouseEvent<HTMLTableRowElement>): void {
    const target = event.target as HTMLElement | null;
    if (target?.closest('button, a, input, select, textarea, label')) return;
    onOpen();
  }

  return (
    <tr className="clickable" onClick={onClick}>
      {children}
    </tr>
  );
}

export interface RowTitleProps {
  onOpen: () => void;
  children: ReactNode;
}

/**
 * The record's name, as the row's keyboard-reachable open control.
 *
 * Styled to look like the text it replaces — the prototype's rows showed a plain
 * `.t-title` — while being a real button, so it is in the tab order, announced
 * as an action, and activated by Enter and Space without any of that being
 * reimplemented.
 */
export function RowTitle({ onOpen, children }: RowTitleProps): ReactElement {
  return (
    <button type="button" className="linklike t-title" onClick={onOpen}>
      {children}
    </button>
  );
}
